import { useLocation, useParams } from 'react-router'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'

const context = createContext<{
  trackScreen: (path: string) => void
  trackEvent: (event: string, data?: Record<string, any>) => void
}>({
  trackScreen: () => {},
  trackEvent: () => {},
})

export default function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation()

  const params = useParams()

  const teamId = useMemo(() => params.teamId ?? '', [params.teamId])

  const trackScreen = useCallback(
    (path: string) => {
      const blob = new Blob(
        [
          JSON.stringify({
            type: 'Screen',
            name: path,
            ...(teamId && { data: { context: { teamId } } }),
          }),
        ],
        { type: 'application/json' }
      )
      navigator.sendBeacon('/t', blob)
    },
    [teamId]
  )

  const trackEvent = useCallback(
    (event: string, data?: Record<string, any>) => {
      const blob = new Blob(
        [JSON.stringify({ type: 'Event', event, path: location.pathname + location.search, data })],
        { type: 'application/json' }
      )
      navigator.sendBeacon('/t', blob)
    },
    [location.pathname, location.search]
  )

  useEffect(() => {
    trackScreen(location.pathname + location.search)
  }, [location.pathname, location.search, trackScreen])

  return <context.Provider value={{ trackScreen, trackEvent }}>{children}</context.Provider>
}

export const useAnalytics = () => {
  return useContext(context)
}
